import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchOrgBranding } from '../../api/learnPublicApi';
import { getCurrentSubdomain } from '../../utils/subdomain';

interface OrgBranding {
  domain: string;
  company_name: string;
  logo: string;
}

interface TrainingTopbarProps {
  subtitle?: string;
  rightContent?: React.ReactNode;
  maxWidth?: number;
  linkToCatalog?: boolean;
  /** Override branding resolved from token-based endpoints (portal, learner page) */
  orgOverride?: OrgBranding | null;
}

const TRAINING_ORG_KEY = 'training_org_domain';

export default function TrainingTopbar({
  subtitle,
  rightContent,
  maxWidth,
  linkToCatalog = true,
  orgOverride,
}: TrainingTopbarProps) {
  const subdomain = getCurrentSubdomain();
  const [searchParams] = useSearchParams();
  const orgFromQuery = searchParams.get('org');
  const storedOrg = (() => { try { return sessionStorage.getItem(TRAINING_ORG_KEY); } catch { return null; } })();
  const orgDomain = subdomain || orgFromQuery || storedOrg || null;
  const [org, setOrg] = useState<OrgBranding | null>(null);
  const catalogPath = useCatalogPath();

  useEffect(() => {
    // Skip domain-based fetch if we have an override
    if (orgOverride) return;
    if (!orgDomain) return;
    fetchOrgBranding(orgDomain)
      .then((data) => {
        if (data?.company_name) setOrg(data);
      })
      .catch(() => {});
  }, [orgDomain, orgOverride]);

  // Use override if provided, otherwise use domain-fetched org
  const resolvedOrg = orgOverride || org;

  // If override provides a domain, persist it for catalog path resolution
  useEffect(() => {
    if (orgOverride?.domain && !subdomain) {
      try { sessionStorage.setItem(TRAINING_ORG_KEY, orgOverride.domain); } catch {}
    }
  }, [orgOverride, subdomain]);

  const brandName = resolvedOrg ? `${resolvedOrg.company_name} Training` : 'Trone Learning';
  const brandSub = subtitle || 'Events';
  const initial = resolvedOrg?.company_name?.charAt(0).toUpperCase() || 'T';

  const logoEl = (
    <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-extrabold text-sm flex-shrink-0 overflow-hidden">
      {resolvedOrg?.logo ? (
        <img src={resolvedOrg.logo} alt="" className="w-5 h-5 object-contain" />
      ) : (
        initial
      )}
    </div>
  );

  const brandContent = (
    <div className="flex items-center gap-2.5">
      {logoEl}
      <div>
        <div className="text-sm font-bold text-gray-900 leading-tight">{brandName}</div>
        <div className="text-[11px] text-gray-400 leading-tight">{brandSub}</div>
      </div>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div
        className="flex items-center justify-between h-14"
        style={maxWidth ? { maxWidth, margin: '0 auto', padding: '0 24px' } : { padding: '0 24px' }}
      >
        {linkToCatalog ? (
          <Link to={catalogPath} className="no-underline flex">
            {brandContent}
          </Link>
        ) : (
          brandContent
        )}
        {rightContent || null}
      </div>
    </header>
  );
}

export function useOrgBranding() {
  const subdomain = getCurrentSubdomain();
  const [org, setOrg] = useState<OrgBranding | null>(null);
  const [orgNotFound, setOrgNotFound] = useState(false);
  const [orgLoading, setOrgLoading] = useState(!!subdomain);

  useEffect(() => {
    if (!subdomain) { setOrgLoading(false); return; }
    setOrgLoading(true);
    setOrgNotFound(false);
    fetchOrgBranding(subdomain)
      .then((data) => {
        if (data?.company_name) {
          setOrg(data);
          setOrgNotFound(false);
        } else {
          setOrg(null);
          setOrgNotFound(true);
        }
      })
      .catch(() => { setOrg(null); setOrgNotFound(true); })
      .finally(() => setOrgLoading(false));
  }, [subdomain]);

  return { subdomain, org, orgNotFound, orgLoading };
}

export type { OrgBranding };

export function useCatalogPath() {
  const subdomain = getCurrentSubdomain();
  const [searchParams] = useSearchParams();
  const orgFromQuery = searchParams.get('org');
  const orgDomain = subdomain || orgFromQuery || null;

  useMemo(() => {
    if (orgDomain && !subdomain) {
      try { sessionStorage.setItem(TRAINING_ORG_KEY, orgDomain); } catch {}
    }
  }, [orgDomain, subdomain]);

  return useMemo(() => {
    if (subdomain) return '/training/catalog';
    const org = orgDomain || (() => { try { return sessionStorage.getItem(TRAINING_ORG_KEY); } catch { return null; } })();
    if (org) return `/training/catalog?org=${encodeURIComponent(org)}`;
    return '/training/catalog';
  }, [orgDomain, subdomain]);
}
